import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Trash2, MessageCircle, AlertCircle, Clock, UserPlus, CheckCheck } from 'lucide-react';
import {
  getInbox, getInboxCount, markNotificationRead, markAllNotificationsRead, deleteNotification
} from '../services/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import { logError } from '../utils/logger';
import { useRealtime } from '../contexts/RealtimeContext';

const TYPE_ICON = {
  ticket_created: AlertCircle,
  ticket_message: MessageCircle,
  ticket_status: Clock,
  ticket_assigned: UserPlus,
};

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return date.toLocaleDateString('pt-BR');
  } catch { return iso; }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { subscribe } = useRealtime();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Subscribe to push notifications via WebSocket
  useEffect(() => {
    const unsub = subscribe('inbox_notification', (msg) => {
      const notif = msg.notification;
      if (!notif) return;
      setItems((prev) => [notif, ...prev].slice(0, 50));
      setUnread((c) => c + 1);
    });
    return unsub;
  }, [subscribe]);

  const refresh = async () => {
    try {
      const [list, count] = await Promise.all([
        getInbox({ limit: 20 }),
        getInboxCount(),
      ]);
      setItems(list.data || []);
      setUnread(count.data?.unread || 0);
    } catch (e) { logError("NotificationBell", e); }
  };

  // Polling silencioso
  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const [list, count] = await Promise.all([
          getInbox({ limit: 20 }),
          getInboxCount(),
        ]);
        if (mounted) {
          setItems(list.data || []);
          setUnread(count.data?.unread || 0);
        }
      } catch (e) { logError("NotificationBell", e); }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const loadOnOpen = async () => {
      if (mounted) setLoading(true);
      try {
        const [list, count] = await Promise.all([
          getInbox({ limit: 20 }),
          getInboxCount(),
        ]);
        if (mounted) {
          setItems(list.data || []);
          setUnread(count.data?.unread || 0);
        }
      } catch (e) { logError("NotificationBell", e); }
      finally {
        if (mounted) setLoading(false);
      }
    };
    loadOnOpen();
    return () => { mounted = false; };
  }, [open]);

  const handleClick = async (n) => {
    if (!n.read) {
      try {
        await markNotificationRead(n.id);
        setUnread((u) => Math.max(0, u - 1));
        setItems((prev) => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      } catch (e) { logError("NotificationBell", e); }
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const handleMarkAll = async (e) => {
    e?.stopPropagation();
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) { logError("NotificationBell", e); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await deleteNotification(id);
      setItems((prev) => prev.filter(n => n.id !== id));
    } catch (e2) { logError("NotificationBell.delete", e2); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 relative"
          data-testid="notifications-btn"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
              data-testid="notifications-unread-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[380px] p-0 bg-white border-gray-200 shadow-xl"
        data-testid="notifications-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#003366]" />
            <h3 className="font-semibold text-gray-900 text-sm">Notificações</h3>
            {unread > 0 && (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                {unread} nova{unread > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAll}
              className="text-xs text-[#003366] hover:bg-blue-50 h-7"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Marcar todas
            </Button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-sm text-gray-400">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-gray-400">
              <Bell className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Nenhuma notificação</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((n) => {
                const Icon = TYPE_ICON[n.type] || Bell;
                return (
                  <li
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${
                      n.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/50 hover:bg-blue-50'
                    }`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className={`mt-0.5 p-1.5 rounded-md ${n.read ? 'bg-gray-100' : 'bg-blue-100'}`}>
                      <Icon className={`w-3.5 h-3.5 ${n.read ? 'text-gray-500' : 'text-[#003366]'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-5 ${n.read ? 'text-gray-700' : 'text-gray-900 font-semibold'}`}>
                          {n.title}
                        </p>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-[#003366] flex-shrink-0 mt-1.5" />}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <span className="text-[10px] text-gray-400 mt-1 block">{formatRelative(n.created_at)}</span>
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, n.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600"
                      title="Remover"
                      data-testid={`delete-notification-${n.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
